import React, { useState, useEffect, useRef, useContext } from 'react'
import PropTypes from 'prop-types'
import { Row, Col } from 'react-flexbox-grid'
import { branch } from 'recompose'
import intersection from 'lodash/intersection'

import Node from './Node'
import SearchInput from 'components/SearchInput'
import Divider from 'components/Divider'
import { Checkbox } from 'components/forms'
import { cleanArray } from 'helpers/array'
import { withFieldSubmit } from 'components/forms/Form/useSmartSubmit'
import { SearchContext } from './searchContext'
import { TreeViewRoot } from './nodes/common'

export const TreeContext = React.createContext()

const Tree = props => {
	const getAllLeafIds = node => {
		let out = []
		if(Array.isArray(node?.children)) {
			node.children.forEach(child => {
				if(child.children) {
					out = [...out, ...getAllLeafIds(child)]
				} else {
					out.push(child.id)
				}
			})
		}
		return out
	}

	const {
		searchValue = '',
		activeNodesArr = [],
		startAllActive,
		hideNoCRBranches,
		startConcealed,
		onUnmount,
		inverted,
		height,
		field,
		allValsRef,
		isSelectable,
		isCheckable,
		queryType, //check do we need it
		showLeafCount,
		searchFunction,
		NodeComponent,
		data: treeData,
		onChangeDelay,
		allCheckBoxLabel,
		allCheckBoxVisible = true
	} = props

	const allLeafIds = getAllLeafIds(treeData)


	const [idArr, setIdArr] = useState(startAllActive ? allLeafIds : activeNodesArr)

	// Can be used in form to get all checked ids if field hasn't changed and startAllActive is true (otherwise on form submit field will be an empty array)
	useEffect(() => {
		if(startAllActive && allValsRef) {
			allValsRef.current = idArr
		}
	}, [startAllActive, allValsRef]) // eslint-disable-line react-hooks/exhaustive-deps

	/* if we have app wide searchContext we use it's state to store search string, otherwise use local state*/
	const [searchStringLocal, setSearchLocal] = useState(searchValue)
	const searchContext = useContext(SearchContext)
	const handleSearch = searchContext ? searchContext.setSearchString : setSearchLocal
	const searchString = searchContext ? searchContext.searchString : searchStringLocal

	const timer = useRef(null)
	const [selectedNode, setSelectedNode] = useState(props.defaultSelected)

	const handleSwitchNode = (touchedIdArr, toggleOn) => {
		if(props.onChange || field) {
			let newIdArr = [...idArr]
			const { onChange, smartSubmit } = props
			newIdArr = toggleOn ? Array.from(new Set(newIdArr.concat(touchedIdArr))) :
				newIdArr.filter(x => !touchedIdArr.includes(x))
			newIdArr = cleanArray(newIdArr)

			if(onChange) {
				clearTimeout(timer.current)
				timer.current = setTimeout(() => onChange(newIdArr), onChangeDelay)
			}

			if(smartSubmit) {
				smartSubmit.submit()
			}
			setIdArr(newIdArr)
		}
	}


	const handleSelect = node => {
		const { onSelect } = props
		if(isSelectable?.__typename && isSelectable?.__typename !== node.__typename) {
			return
		}
		if(onSelect) {
			setSelectedNode(node)
			onSelect(node)
		}
	}

	const enchanceTreeData = node => {
		const newNode = {
			...node,
			leafIds: [],
			allChildren: []
		}

		if(newNode.children) {
			newNode.children = newNode.children.map(child => {
				const newChild = enchanceTreeData(child)
				newNode.leafIds = newNode.leafIds.concat(newChild.leafIds)
				newNode.allChildren.push(newChild)
				newNode.allChildren = newNode.allChildren.concat(newChild.allChildren)
				return newChild
			})
		} else {
			newNode.leafIds = [newNode.id]
		}
		return newNode
	}

	const contextValue = {
		//parent data
		treeData,
		NodeComponent,
		// options
		isSelectable,
		isCheckable,
		showLeafCount,
		hideNoCRBranches,
		inverted,
		searchFunction,
		startConcealed: searchString ? false : startConcealed,
		// state
		activeLeafs: idArr,
		selectedNode,
		// api
		handleSwitchNode,
		handleSelect
	}


	const idIntersection = intersection(allLeafIds, idArr)
	const allCheckboxStatus = allLeafIds.length && idIntersection.length === allLeafIds.length ? 'ACTIVE' : idIntersection.length ? 'PARTIAL_ACTIVE' : 'INACTIVE'

	return (
		<TreeContext.Provider value={contextValue}>
			<Row>
				<Col xs={12}>
					<SearchInput value={searchString} onSearch={handleSearch} onUnmount={onUnmount} inverted={inverted} />
				</Col>
			</Row>
			<Divider height={32}/>
			{allCheckBoxVisible && <Checkbox
				noForm
				stopPropagation={true}
				onChange={e => handleSwitchNode(allLeafIds, e.target.checked)}
				checked={allCheckboxStatus === 'ACTIVE'}
				indeterminate={allCheckboxStatus === 'PARTIAL_ACTIVE'}
				label={allCheckBoxLabel}
				inline
			/>}
			<TreeViewRoot maxheight={height} inverted={inverted} >
				<Node
					depth={1}
					key={searchString}
					nodeData={enchanceTreeData(treeData)}
					searchStr={searchString.toLowerCase()}
					queryType={queryType}//check
				/>
			</TreeViewRoot>
		</TreeContext.Provider>
	)
}

Tree.propTypes = {
	NodeComponent: PropTypes.func.isRequired,
	activeNodesArr: PropTypes.array,
	hideNoCRBranches: PropTypes.bool,
	defaultSelected: PropTypes.shape({
		id: PropTypes.string,
		__typename: PropTypes.string
	}),
	field: PropTypes.string, // If defined smartSubmit will be triggered on change
	onSelect: PropTypes.func,
	onChange: PropTypes.func,
	onChangeDelay: PropTypes.number,
	startConcealed: PropTypes.bool,
	startAllActive: PropTypes.bool,
	height: PropTypes.string,
	withContext: PropTypes.bool, // Use and update section context to store search value if true
	searchValue: PropTypes.string,
	allValsRef: PropTypes.object,
	isCheckable: PropTypes.bool,
	showLeafCount: PropTypes.bool,
	isSelectable: PropTypes.oneOfType([PropTypes.bool, PropTypes.shape({
		__typename: PropTypes.string
	})]),
	searchFunction: PropTypes.func, // provide custom function to test if node is matching search string
	allCheckBoxLabel: PropTypes.string,
	allCheckBoxVisible: PropTypes.bool
}

Tree.defaultProps = {
	activeNodesArr: [],
	startConcealed: true,
	hideNoCRBranches: true,
	isCheckable: true,
	showLeafCount: true,
	onChangeDelay: 1000
}

export default branch(({ field }) => field,
	withFieldSubmit
)(Tree)
